import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Building2, CreditCard, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface RealEstateProfile {
  name: string;
  email: string;
  phone: string;
  address: string;
  adminName: string;
  website: string;
  password: string;
}

const SettingsPage = () => {
  const [profile, setProfile] = useState<RealEstateProfile>(() => {
    const saved = localStorage.getItem('realEstateProfile');
    return saved ? JSON.parse(saved) : {
      name: 'VisitVila',
      email: 'contato@visitvila.com.br',
      phone: '(11) 99999-9999',
      address: 'Rua das Imobiliárias, 123',
      adminName: 'João Silva',
      website: 'www.visitvila.com.br',
      password: ''
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSave = () => {
    localStorage.setItem('realEstateProfile', JSON.stringify(profile));
    setIsEditing(false);
    toast.success("Perfil atualizado com sucesso!");
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-heading font-bold text-gray-900">Configurações</h1>
        <p className="mt-2 text-gray-600">Gerencie as configurações do sistema</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Perfil da Imobiliária</TabsTrigger>
          <TabsTrigger value="plan">Plano e Pagamento</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl">Perfil da Imobiliária</CardTitle>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsEditing(!isEditing)}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nome da Imobiliária</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Nome do Administrador</Label>
                <Input
                  value={profile.adminName}
                  onChange={(e) => setProfile({ ...profile, adminName: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={profile.website}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  disabled={!isEditing}
                  placeholder="www.seusite.com.br"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Endereço</Label>
                <Input
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div>
                <Label>Senha</Label>
                <div className="flex gap-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={profile.password}
                    onChange={(e) => setProfile({ ...profile, password: e.target.value })}
                    disabled={!isEditing}
                    placeholder={isEditing ? "Digite a nova senha" : "••••••••"}
                  />
                  {isEditing && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "Ocultar" : "Mostrar"}
                    </Button>
                  )}
                </div>
              </div>
              {isEditing && (
                <Button onClick={handleSave} className="w-full">
                  Salvar Alterações
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plan">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Plano e Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Plano Atual</span>
                  <span className="text-primary">Profissional</span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Próxima cobrança</span>
                  <span>15/05/2024</span>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <h3 className="font-medium">Método de Pagamento</h3>
                <div className="flex items-center space-x-4 rounded-lg border p-4">
                  <CreditCard className="h-6 w-6 text-gray-400" />
                  <div>
                    <p className="font-medium">Cartão de Crédito</p>
                    <p className="text-sm text-gray-600">**** **** **** 4242</p>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  Atualizar Método de Pagamento
                </Button>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Faturas</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Abril 2024</span>
                    <Button variant="link" size="sm">Download</Button>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Março 2024</span>
                    <Button variant="link" size="sm">Download</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;